import React from 'react';
import { CheckCircle2, Award, Users, Target, Factory, Calendar, Phone, Building2 } from 'lucide-react';
import { companyInfo, chairman, headOfOperations, companyHistory, statistics, capabilities, workforce, sisterConcern } from '../data/mock';

const About = () => {
  return (
    <div className="bg-[#0b0b0b]">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px),
                             repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)`
          }} />
        </div>
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#b00000] via-red-600 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">About the Company</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              {companyInfo.tagline}
            </h1>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed">
              {companyInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-12 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-start">
            {/* Timeline */}
            <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-8">
              <h3 className="text-white font-semibold text-base sm:text-lg mb-4 sm:mb-6 flex items-center gap-2">
                <Calendar className="w-4 sm:w-5 h-4 sm:h-5 text-[#b00000]" />
                Company Milestones
              </h3>
              <div className="space-y-4 sm:space-y-6">
                {companyHistory.map((item, index) => (
                  <div key={index} className="flex gap-3 sm:gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-2 sm:w-3 h-2 sm:h-3 bg-[#b00000] rounded-none full" />
                      {index < companyHistory.length - 1 && (
                        <div className="w-px h-full bg-white/20 mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-4 sm:pb-6">
                      <div className="text-[#b00000] font-semibold mb-1 text-sm sm:text-base">{item.year}</div>
                      <p className="text-white/70 text-xs sm:text-sm">{item.milestone}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Our Journey</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
                Since 1988 — A Legacy of Excellence
              </h2>
              <p className="text-white/70 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
                Our story began in 1988 with the goal of enhancing Coal Handling Plant (CHP) operations 
                in Thermal Power Stations. Starting with Wagon Tippler maintenance, we've 
                evolved into a prominent name, offering complete CHP services across the country.
              </p>
              <p className="text-white/70 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
                With over three decades of experience, we have established ourselves as a trusted partner 
                for major National Power Corporation units including NTPC and NSPCL. Our commitment to 
                quality, efficiency, and reliability has earned us the trust of India's leading power 
                generation companies.
              </p>
              
              {/* Mission & Vision */}
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <Target className="w-5 sm:w-6 h-5 sm:h-6 text-[#b00000]" />
                    <h3 className="text-white font-semibold text-base sm:text-lg">Our Mission</h3>
                  </div>
                  <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                    {companyInfo.mission}
                  </p>
                </div>
                <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <Award className="w-5 sm:w-6 h-5 sm:h-6 text-[#b00000]" />
                    <h3 className="text-white font-semibold text-base sm:text-lg">Our Vision</h3>
                  </div>
                  <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                    {companyInfo.vision}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Section - Both profiles same style */}
      <section className="py-12 sm:py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Leadership</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Our Leadership Team</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-8 max-w-4xl mx-auto">
            {/* Mr. Raju B. Tandale */}
            <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-8 text-center hover:border-[#b00000]/50 transition-colors">
              <div className="w-28 h-28 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6 rounded-full overflow-hidden border-4 border-[#b00000]/50 bg-black isolate">
                <img 
                  src="/images/raju_tandale.jpeg" 
                  alt="Mr. Raju B. Tandale"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{chairman.name}</h3>
              <p className="text-[#b00000] text-xs sm:text-sm mb-3 sm:mb-4">{chairman.title}</p>
              <div className="space-y-1 sm:space-y-2">
                {chairman.phones.map((phone, idx) => (
                  <a 
                    key={idx} 
                    href={`tel:+91${phone}`}
                    className="flex items-center justify-center gap-2 text-white/60 text-xs sm:text-sm hover:text-white transition-colors"
                  >
                    <Phone className="w-3 sm:w-4 h-3 sm:h-4 text-[#b00000]" />
                    <span>+91 {phone}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Mr. Akash R. Tandale */}
            <div className="bg-[#0b0b0b] border border-white/10 p-4 sm:p-8 text-center hover:border-[#b00000]/50 transition-colors">
              <div className="w-28 h-28 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6 rounded-full overflow-hidden border-4 border-[#b00000]/50 bg-[#0b0b0b]">
                <img 
                  src="/images/akash_tandale.jpeg" 
                  alt="Mr. Akash R. Tandale"
                  className="w-full h-full object-cover object-center"
                  style={{ imageRendering: 'auto' }}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{headOfOperations.name}</h3>
              <p className="text-[#b00000] text-xs sm:text-sm mb-3 sm:mb-4">{headOfOperations.title}</p>
              <div className="space-y-1 sm:space-y-2">
                {headOfOperations.phones.map((phone, idx) => (
                  <a 
                    key={idx} 
                    href={`tel:+91${phone}`}
                    className="flex items-center justify-center gap-2 text-white/60 text-xs sm:text-sm hover:text-white transition-colors"
                  >
                    <Phone className="w-3 sm:w-4 h-3 sm:h-4 text-[#b00000]" />
                    <span>+91 {phone}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Our Strength in Numbers</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Company Experience & Credibility</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {statistics.slice(0, 4).map((stat, index) => (
              <div 
                key={index}
                className="bg-[#0b0b0b] border border-white/10 p-8 text-center hover:border-[#b00000]/50 transition-colors"
              >
                <div className="text-3xl sm:text-4xl font-bold text-[#b00000] mb-2">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workforce & Sister Concern */}
      <section className="py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Workforce */}
            <div>
              <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Workforce</span>
              <h2 className="text-2xl font-bold text-white mb-6">Our Dedicated Team</h2>
              <div className="space-y-4">
                <div className="bg-[#0b0b0b] border border-white/10 p-6">
                  <div className="text-2xl font-bold text-[#b00000] mb-2">500+</div>
                  <p className="text-white/70">Total Workforce</p>
                </div>
                <div className="bg-[#0b0b0b] border border-white/10 p-6">
                  <div className="text-2xl font-bold text-[#b00000] mb-2">25+</div>
                  <p className="text-white/70">Specialized maintenance professionals</p>
                </div>
              </div>
              <p className="text-white/60 text-sm mt-6">
                {workforce.description}
              </p>
            </div>
            
            {/* Sister Concern */}
            <div>
              <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Sister Concern</span>
              <h2 className="text-2xl font-bold text-white mb-6">{sisterConcern.name}</h2>
              <div className="bg-[#0b0b0b] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-8 h-8 text-[#b00000]" />
                  <div>
                    <p className="text-white font-semibold">Established {sisterConcern.established}</p>
                  </div>
                </div>
                <p className="text-white/70 mb-6">{sisterConcern.description}</p>
                <div className="space-y-2">
                  {sisterConcern.services.map((service, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#b00000]" />
                      <span className="text-white/70 text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Our Expertise</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Core Capabilities
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              We specialize in comprehensive solutions for thermal power plants and coal handling facilities.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {capabilities.map((capability, index) => (
              <div 
                key={index}
                className="bg-[#0b0b0b] border border-white/10 p-4 flex items-center gap-3 hover:border-[#b00000]/50 transition-colors group"
              >
                <CheckCircle2 className="w-5 h-5 text-[#b00000] flex-shrink-0" />
                <span className="text-white/70 text-sm group-hover:text-white transition-colors">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The ElemecH Advantage
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: 'Since 1988', desc: '35+ years of specialized experience in thermal power sector' },
              { icon: Users, title: 'Skilled Workforce', desc: '500+ trained professionals across multiple disciplines' },
              { icon: Target, title: 'NTPC & NSPCL Enlisted', desc: 'Trusted by major National Power Corporation units' },
              { icon: Factory, title: 'Complete Solutions', desc: 'End-to-end CHP services from maintenance to manufacturing' }
            ].map((item, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 p-6 hover:border-[#b00000]/50 transition-colors group">
                <div className="w-12 h-12 bg-[#b00000]/20 flex items-center justify-center mb-4 group-hover:bg-[#b00000] transition-colors">
                  <item.icon className="w-6 h-6 text-[#b00000] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
